import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, ShieldCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

export const VERIFICATION_TYPES = [
  'SBA VetCert / VOSB / SDVOSB',
  'State or local veteran-owned business certification',
  'Other veteran-owned business certification',
];
const EMPTY = { full_name: '', business_name: '', business_email: '', business_website: '', phone: '', verification_type: '', certification_number: '', confirmed: false };
const MAX_SIZE = 10 * 1024 * 1024;

export function validateVeteranForm(values, file) {
  const errors = {};
  if (!values.full_name.trim()) errors.full_name = 'Enter your full name.';
  if (!values.business_name.trim()) errors.business_name = 'Enter your business name.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.business_email.trim())) errors.business_email = 'Enter a valid business email.';
  if (!VERIFICATION_TYPES.includes(values.verification_type)) errors.verification_type = 'Select a verification type.';
  if (values.business_website.trim()) {
    try {
      const url = new URL(values.business_website.trim());
      if (!['http:', 'https:'].includes(url.protocol) || !url.hostname || url.username || url.password || /\s/.test(values.business_website.trim())) throw new Error();
    } catch { errors.business_website = 'Enter a valid http:// or https:// website URL.'; }
  }
  if (!file) errors.document = 'Please upload a supporting document.';
  else if (/\.(bat|cmd|exe|com|scr|msi|ps1|vbs|vbe|js|jse|hta|sh|py|php|dll)(\.|$)/i.test(file.name)) errors.document = 'Executable and script files are not allowed. Upload a PDF, JPG or PNG.';
  else if (!/\.(pdf|jpe?g|png)$/i.test(file.name) || (file.type && !['application/pdf', 'image/jpeg', 'image/png'].includes(file.type))) errors.document = 'Upload a PDF, JPG, JPEG or PNG.';
  else if (!file.size || file.size > MAX_SIZE) errors.document = 'Upload a non-empty document no larger than 10 MB.';
  if (!values.confirmed) errors.confirmed = 'Please confirm that the information is accurate.';
  return errors;
}

export default function VeteranVerificationModal({ open, onOpenChange, triggerRef }) {
  const [values, setValues] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const lock = useRef(false);
  const formRef = useRef(null);

  const setValue = (key, value) => {
    setValues(previous => ({ ...previous, [key]: value }));
    setErrors(previous => ({ ...previous, [key]: undefined, form: undefined }));
  };
  const showErrors = (next) => {
    setErrors(next);
    requestAnimationFrame(() => formRef.current?.querySelector('[aria-invalid="true"]')?.focus());
  };
  const submit = async (event) => {
    event.preventDefault();
    if (lock.current) return;
    const next = validateVeteranForm(values, file);
    if (Object.keys(next).length) return showErrors(next);
    lock.current = true;
    setSubmitting(true);
    setErrors({});
    const body = new FormData();
    Object.entries(values).forEach(([key, value]) => body.append(key, typeof value === 'string' ? value.trim() : String(value)));
    body.append('document', file);
    try {
      await axios.post('/veteran-verifications', body);
      setSubmitted(true);
      setFile(null);
      setValues(EMPTY);
    } catch (error) {
      const detail = error.response?.data?.detail;
      showErrors(detail && typeof detail === 'object' && !Array.isArray(detail)
        ? detail : { form: typeof detail === 'string' ? detail : 'We could not submit your request. Please try again.' });
    } finally {
      lock.current = false;
      setSubmitting(false);
    }
  };
  const error = (key) => errors[key] && <p id={`veteran-${key}-error`} className="text-sm text-red-700 mt-1" role="alert">{errors[key]}</p>;
  const attrs = (key) => ({ id: `veteran-${key}`, name: key, 'aria-invalid': !!errors[key], 'aria-describedby': errors[key] ? `veteran-${key}-error` : undefined });
  const fields = [
    ['full_name', 'Full Name', true, 'text', 120, 'name'],
    ['business_name', 'Business Name', true, 'text', 200, 'organization'],
    ['business_email', 'Business Email', true, 'email', 254, 'email'],
    ['business_website', 'Business Website', false, 'url', 2048, 'url'],
    ['phone', 'Phone Number', false, 'tel', 40, 'tel'],
    ['certification_number', 'Certification / Verification Number', false, 'text', 150, 'off'],
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent overlayClassName="z-[10020]" className="z-[10021] w-[calc(100%-2rem)] max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl bg-[#FFFCF7] text-[#021426] p-5 sm:p-8 [&>button:last-child]:w-11 [&>button:last-child]:h-11 [&>button:last-child]:flex [&>button:last-child]:items-center [&>button:last-child]:justify-center [&>button:last-child]:top-1 [&>button:last-child]:right-1"
        onCloseAutoFocus={event => { event.preventDefault(); triggerRef?.current?.focus(); }}>
        <DialogHeader className="text-left pr-8">
          <ShieldCheck className="w-8 h-8 text-[#b66a09] mb-2" />
          <DialogTitle className="text-2xl leading-tight">{submitted ? 'Verification submitted' : 'Verify your veteran-owned business'}</DialogTitle>
          <DialogDescription className="text-[#4C545B] leading-relaxed">
            {submitted ? 'Thanks! We’ve received your veteran-owned business verification request. Our team will review your information and contact you at the email provided.' : 'Submit your business details and verification information. Once approved, your 20% veteran discount will be applied to your account for as long as you remain a customer.'}
          </DialogDescription>
        </DialogHeader>
        {submitted ? <Button className="min-h-11 bg-[#FEAB3D] text-[#021426] hover:bg-[#e8960a] rounded-xl" onClick={() => onOpenChange(false)}>Close</Button> : (
          <form ref={formRef} onSubmit={submit} noValidate className="space-y-5">
            <p className="text-xs text-slate-600">Fields marked * are required. Your request will be reviewed before any discount becomes available.</p>
            {errors.form && <p role="alert" className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">{errors.form}</p>}
            <fieldset disabled={submitting} className="grid grid-cols-1 sm:grid-cols-2 gap-4 disabled:opacity-70">
              {fields.slice(0, 5).map(([key, label, required, type, maxLength, autoComplete]) => (
                <div key={key} className="min-w-0">
                  <Label htmlFor={`veteran-${key}`} className="block mb-1.5">{label}{required ? ' *' : ''}</Label>
                  <Input {...attrs(key)} type={type} maxLength={maxLength} autoComplete={autoComplete} required={required} value={values[key]} onChange={e => setValue(key, e.target.value)} className="min-h-11 bg-white text-base rounded-lg" />
                  {error(key)}
                </div>
              ))}
              <div className="min-w-0">
                <Label htmlFor="veteran-verification_type" className="block mb-1.5">Verification Type *</Label>
                <select {...attrs('verification_type')} required value={values.verification_type} onChange={e => setValue('verification_type', e.target.value)} className="w-full min-h-11 border border-slate-300 rounded-lg bg-white text-base px-3">
                  <option value="">Select verification type</option>
                  {VERIFICATION_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
                {error('verification_type')}
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="veteran-certification_number" className="block mb-1.5">Certification / Verification Number</Label>
                <Input {...attrs('certification_number')} maxLength={150} value={values.certification_number} onChange={e => setValue('certification_number', e.target.value)} className="min-h-11 bg-white text-base rounded-lg" />
                {error('certification_number')}
              </div>
              <div className="sm:col-span-2 min-w-0">
                <Label htmlFor="veteran-document" className="block mb-1.5">Supporting Document *</Label>
                <input {...attrs('document')} aria-describedby={`veteran-document-help${errors.document ? ' veteran-document-error' : ''}`} type="file" required accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={e => { setFile(e.target.files?.[0] || null); setErrors(previous => ({ ...previous, document: undefined })); }} className="w-full min-h-11 max-w-full text-sm border border-slate-300 rounded-lg p-2 bg-white text-[#021426] file:mr-2 file:border-0 file:rounded file:bg-[#021426] file:text-white file:font-semibold file:cursor-pointer hover:file:bg-[#0C5B94] file:px-3 file:py-2" />
                <p id="veteran-document-help" className="text-xs text-slate-600 mt-2 leading-relaxed">PDF, JPG, JPEG or PNG, maximum 10 MB. Please upload business verification or certification documentation only. Do not upload documents containing sensitive personal military information such as a DD-214.</p>
                {error('document')}
              </div>
              <div className="sm:col-span-2">
                <label className="flex gap-3 items-start text-sm leading-relaxed cursor-pointer">
                  <input {...attrs('confirmed')} required type="checkbox" checked={values.confirmed} onChange={e => setValue('confirmed', e.target.checked)} className="mt-1 w-5 h-5 shrink-0 accent-[#021426]" />
                  <span>I confirm that this business is veteran-owned and that the information submitted is accurate.</span>
                </label>
                {error('confirmed')}
              </div>
            </fieldset>
            <Button type="submit" disabled={submitting} className="w-full min-h-12 whitespace-normal rounded-xl bg-[#FEAB3D] text-[#021426] hover:bg-[#e8960a] font-bold">
              {submitting && <Loader2 className="animate-spin mr-2 w-4 h-4" />}{submitting ? 'Submitting…' : 'Submit for verification'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
